import React, { useEffect, useRef, useState } from 'react';

export interface ModelOption {
  _id: string;
  name: string;
  code: string;
  factory_name?: string;
}

interface ModelMeta {
  image: string;
  leaves?: number;
}

// Per-model presentation metadata keyed by the model's code.
const MODEL_META: Record<string, ModelMeta> = {
  fluora_mini: { image: '/models/fluora-mini.png', leaves: 9 },
  fluora_floor: { image: '/models/fluora-floor.png', leaves: 17 },
  monos: { image: '/models/monos.png' },
};

export const getModelImage = (code: string): string | undefined => MODEL_META[code]?.image;

export const getModelLabel = (model: ModelOption, useFactoryName = false): string => {
  const base = useFactoryName && model.factory_name ? model.factory_name : model.name;
  const leaves = MODEL_META[model.code]?.leaves;
  return leaves ? `${base} (${leaves} leaves)` : base;
};

// Same as getModelLabel but renders the "(N leaves)" suffix in italics.
export const renderModelLabel = (model: ModelOption, useFactoryName = false): React.ReactNode => {
  const base = useFactoryName && model.factory_name ? model.factory_name : model.name;
  const leaves = MODEL_META[model.code]?.leaves;
  if (!leaves) return <strong>{base}</strong>;
  return (
    <>
      <strong>{base}</strong> <em>({leaves} leaves)</em>
    </>
  );
};

interface ModelSelectProps {
  models: ModelOption[];
  value: string; // selected model code, "" when nothing is selected
  onChange: (code: string) => void;
  id?: string;
  placeholder?: string;
  useFactoryName?: boolean;
  className?: string;
}

const ModelThumb: React.FC<{ code: string; size: number }> = ({ code, size }) => {
  const src = getModelImage(code);
  if (!src) {
    return (
      <span
        className="d-inline-flex align-items-center justify-content-center bg-light rounded flex-shrink-0"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="rounded flex-shrink-0"
      style={{ width: size, height: size, objectFit: 'cover' }}
    />
  );
};

const ModelSelect: React.FC<ModelSelectProps> = ({
  models,
  value,
  onChange,
  id,
  placeholder = 'Choose Your Device...',
  useFactoryName = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const selectedModel = models.find((m) => m.code === value);

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div className={`position-relative ${className}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        className="form-select text-start d-flex align-items-center"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ gap: '0.5rem', minHeight: '38px' }}
      >
        {selectedModel ? (
          <>
            <ModelThumb code={selectedModel.code} size={32} />
            <span className="text-truncate">{renderModelLabel(selectedModel, useFactoryName)}</span>
          </>
        ) : (
          <span className="text-muted">{placeholder}</span>
        )}
      </button>

      {open && (
        <ul
          className="list-unstyled shadow-sm bg-white border rounded mb-0 py-1"
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: '320px',
            overflowY: 'auto',
          }}
        >
          {models.map((model) => {
            const isSelected = model.code === value;
            return (
              <li key={model._id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`btn w-100 text-start d-flex align-items-center border-0 rounded-0 px-3 py-2 ${
                    isSelected ? 'bg-primary-subtle fw-semibold' : ''
                  }`}
                  onClick={() => handleSelect(model.code)}
                  style={{ gap: '0.75rem' }}
                >
                  <ModelThumb code={model.code} size={52} />
                  <span>{renderModelLabel(model, useFactoryName)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ModelSelect;
